import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaHome,
  FaUserMd,
  FaCogs,
  FaInfoCircle,
  FaComments,
  FaStar,
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaClock,
  FaShieldAlt,
  FaFileContract,
  FaSitemap,
} from "react-icons/fa";
import { CLINIC_GOOGLE_MAPS_LINK } from "../../utils/addressUtils";
import {
  HELPDESK_PHONE_DISPLAY,
  EMERGENCY_PHONE_DISPLAY,
  HELPDESK_PHONE_TEL,
  EMERGENCY_PHONE_TEL,
} from "../../utils/clinicContact";
import "./Sitemap.css";

const Sitemap = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="sitemap-page">
      {/* Hero Section */}
      <section className="sitemap-hero">
        <div className="container">
          <motion.div
            className="sitemap-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Website Sitemap</h1>
            <p>Complete navigation guide to all pages and sections</p>
            <div className="sitemap-badge">
              <FaSitemap className="sitemap-icon" />
              <span>Last Updated: {currentDate}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container">
        {/* Main Navigation */}
        <section className="sitemap-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Main Navigation</h2>
            <p>Primary pages and sections of our website</p>
          </motion.div>

          <div className="sitemap-grid">
            {/* Home Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card-header">
                <FaHome className="card-icon" />
                <h3>Home</h3>
              </div>
              <div className="card-content">
                <Link to="/" className="page-link" style={{ color: "white" }}>
                  Visit Home Page
                </Link>
                <p>Main landing page with clinic overview and key services</p>
                <div className="page-sections">
                  <h4>Page Sections:</h4>
                  <ul>
                    <li>Hero Banner</li>
                    <li>About Dr. Satya Sudhish Nimmagadda</li>
                    <li>Key Services</li>
                    <li>Why Choose Us</li>
                    <li>Contact Information</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* About Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="card-header">
                <FaUserMd className="card-icon" />
                <h3>About Us</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/about"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit About Page
                </Link>
                <p>Comprehensive information about Dr. Satya and the clinic</p>
                <div className="page-sections">
                  <h4>Page Sections:</h4>
                  <ul>
                    <li>Doctor Profile</li>
                    <li>Professional Credentials</li>
                    <li>Areas of Specialization</li>
                    <li>Professional Achievements</li>
                    <li>Research Papers</li>
                    <li>Clinic Mission</li>
                    <li>Patient Care Philosophy</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Services Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="card-header">
                <FaCogs className="card-icon" />
                <h3>Services</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/services"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Services Page
                </Link>
                <p>Complete list of cardiac care services offered</p>
                <div className="page-sections">
                  <h4>Service Categories:</h4>
                  <ul>
                    <li>Interventional Cardiology</li>
                    <li>Diagnostic Services</li>
                    <li>Preventive Cardiology</li>
                    <li>Emergency Cardiac Care</li>
                    <li>Follow-up Care</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Patient Info Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="card-header">
                <FaInfoCircle className="card-icon" />
                <h3>Patient Information</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/patient-info"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Patient Info Page
                </Link>
                <p>Important information for patients and visitors</p>
                <div className="page-sections">
                  <h4>Page Sections:</h4>
                  <ul>
                    <li>Appointment Booking</li>
                    <li>What to Bring</li>
                    <li>Preparing for Visit</li>
                    <li>Insurance Information</li>
                    <li>Payment Options</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Testimonials Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="card-header">
                <FaStar className="card-icon" />
                <h3>Testimonials</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/testimonials"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Testimonials Page
                </Link>
                <p>Patient experiences and success stories</p>
                <div className="page-sections">
                  <h4>Page Sections:</h4>
                  <ul>
                    <li>Patient Stories</li>
                    <li>Success Cases</li>
                    <li>Patient Reviews</li>
                    <li>Before & After</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Contact Page */}
            <motion.div
              className="sitemap-card main-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="card-header">
                <FaComments className="card-icon" />
                <h3>Contact Us</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/contact"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Contact Page
                </Link>
                <p>Get in touch and book appointments</p>
                <div className="page-sections">
                  <h4>Page Sections:</h4>
                  <ul>
                    <li>Contact Form</li>
                    <li>Appointment Booking</li>
                    <li>Location & Map</li>
                    <li>Contact Details</li>
                    <li>Operating Hours</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Legal Pages */}
        <section className="sitemap-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Legal & Policy Pages</h2>
            <p>Important legal information and policies</p>
          </motion.div>

          <div className="sitemap-grid legal-pages">
            <motion.div
              className="sitemap-card legal-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card-header">
                <FaShieldAlt className="card-icon" />
                <h3>Privacy Policy</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/privacy"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Privacy Page
                </Link>
                <p>How we protect and handle your personal information</p>
              </div>
            </motion.div>

            <motion.div
              className="sitemap-card legal-page"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="card-header">
                <FaFileContract className="card-icon" />
                <h3>Terms of Service</h3>
              </div>
              <div className="card-content">
                <Link
                  to="/terms"
                  className="page-link"
                  style={{ color: "white" }}
                >
                  Visit Terms Page
                </Link>
                <p>Terms and conditions for using our services</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="sitemap-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Contact Information</h2>
            <p>How to reach us and get in touch</p>
          </motion.div>

          <div className="contact-info-grid">
            <motion.div
              className="contact-info-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <FaPhone className="contact-icon" />
              <h3>Phone</h3>
              <p>
                <a href={`tel:${HELPDESK_PHONE_TEL}`}>
                  Helpdesk: {HELPDESK_PHONE_DISPLAY}
                </a>
              </p>
              <p>
                <a href={`tel:${EMERGENCY_PHONE_TEL}`}>
                  Emergency: {EMERGENCY_PHONE_DISPLAY}
                </a>
              </p>
            </motion.div>

            <motion.div
              className="contact-info-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <FaEnvelope className="contact-icon" />
              <h3>Email</h3>
              <p>satya.s.nimmagadda@gmail.com</p>
              <small>We respond within 24 hours</small>
            </motion.div>

            <motion.div
              className="contact-info-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FaMapMarkerAlt className="contact-icon" />
              <h3>Address</h3>
              <p>
                <a
                  href={CLINIC_GOOGLE_MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="address-link"
                  title="Click to open in Google Maps"
                >
                  Shop No-6, Shopping Complex, Vasanth Nagar, IDPL Staff
                  Cooperative Housing Society, Vasanth Nagar, Kukatpally,
                  Hyderabad, Telangana 500085
                </a>
              </p>
            </motion.div>

            <motion.div
              className="contact-info-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FaClock className="contact-icon" />
              <h3>Operating Hours</h3>
              <p>
                <strong>Monday to Saturday:</strong> 6:00 PM - 9:00 PM
              </p>
              <small>Emergency care available 24/7</small>
            </motion.div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="sitemap-section">
          <motion.div
            className="quick-links"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Quick Navigation</h3>
            <div className="quick-links-grid">
              <Link to="/" className="quick-link">
                🏠 Home
              </Link>
              <Link to="/about" className="quick-link">
                👨‍⚕️ About
              </Link>
              <Link to="/services" className="quick-link">
                🔧 Services
              </Link>
              <Link to="/patient-info" className="quick-link">
                📋 Patient Info
              </Link>
              <Link to="/testimonials" className="quick-link">
                ⭐ Testimonials
              </Link>
              <Link to="/contact" className="quick-link">
                📞 Contact
              </Link>
              <Link to="/privacy" className="quick-link">
                🔒 Privacy
              </Link>
              <Link to="/terms" className="quick-link">
                📄 Terms
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default Sitemap;
